/**
 * Product management interface for admins
 * Follows the unified UX pattern with stats, filtering, and modern design
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { adminApi, productsApi } from '../../services/api';
import { generateFieldSuggestion, GenerationContext, checkOllamaConnection } from '../../services/ollama';
import { Product, Currency, PaginatedResponse } from '../../types';
import PermissionChecker from './PermissionChecker';
import { FilterBar, FilterConfig } from '../shared/FilterBar';
import { Pagination } from '../shared/Pagination';
import { useAdminFilters } from './shared/hooks/useAdminFilters';
import { useSelection } from '../../hooks/useSelection';
import DataTable, { Column } from '../shared/DataTable';
import BulkActionToolbar, { BulkActionButton } from '../shared/BulkActionToolbar';
import { StatsWidget, StatItem } from '../common/StatsWidget';
import { AiAssistButton } from '../common/AiAssistButton';
import {
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  Package,
  CheckCircle,
  FileText,
  Plus,
  RefreshCw,
  HelpCircle,
} from 'lucide-react';

// Filter configuration for products
const productFilters: FilterConfig[] = [
  {
    type: 'search',
    label: 'Search',
    key: 'search',
    placeholder: 'Search title, description, keywords...',
    width: 'lg',
  },
  {
    type: 'select',
    label: 'Category',
    key: 'category',
    width: 'md',
    options: [
      { value: '', label: 'All Categories' },
      { value: 'horse-racing', label: 'Horse Racing' },
      { value: 'greyhound-racing', label: 'Greyhound Racing' },
      { value: 'lottery', label: 'Lottery' },
      { value: 'card-games', label: 'Card Games' },
      { value: 'roulette', label: 'Roulette' },
      { value: 'multi-game', label: 'Multi-Game' },
    ],
  },
  {
    type: 'select',
    label: 'Status',
    key: 'published',
    width: 'sm',
    options: [
      { value: '', label: 'All' },
      { value: 'true', label: 'Published' },
      { value: 'false', label: 'Draft' },
    ],
  },
  {
    type: 'select',
    label: 'Quality Tier',
    key: 'qualityTier',
    width: 'sm',
    options: [
      { value: '', label: 'All' },
      { value: 'low', label: 'Low' },
      { value: 'basic', label: 'Basic' },
      { value: 'premium', label: 'Premium' },
    ],
  },
  {
    type: 'number-range',
    label: 'Price Range',
    key: 'price',
    width: 'md',
  },
  {
    type: 'date-range',
    label: 'Created Date',
    key: 'date',
    width: 'md',
  },
];

export default function ProductManagement(): JSX.Element {
  const [response, setResponse] = useState<PaginatedResponse<Product> | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showQualityModal, setShowQualityModal] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newQualityTier, setNewQualityTier] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Selection state for bulk actions
  const { selectedIds, toggleSelection, toggleAll, clearSelection, selectedCount } = useSelection();

  // Use the admin filters hook with URL synchronization
  const { filters, debouncedFilters, setFilter, clearFilters } = useAdminFilters({
    page: 1,
    limit: 25,
    search: '',
    category: '',
    published: '',
    qualityTier: '',
    priceMin: '',
    priceMax: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Load products whenever debounced filters change
  useEffect(() => {
    loadProducts();
  }, [debouncedFilters]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      // Build API params from filters
      const params: any = {
        page: debouncedFilters.page,
        limit: debouncedFilters.limit,
        sortBy: debouncedFilters.sortBy,
        sortOrder: debouncedFilters.sortOrder,
      };

      if (debouncedFilters.search) params.search = debouncedFilters.search;
      if (debouncedFilters.category) params.category = debouncedFilters.category;
      if (debouncedFilters.published !== '') params.published = debouncedFilters.published === 'true';
      if (debouncedFilters.qualityTier) params.qualityTier = debouncedFilters.qualityTier;
      if (debouncedFilters.priceMin) params.priceMin = Number(debouncedFilters.priceMin);
      if (debouncedFilters.priceMax) params.priceMax = Number(debouncedFilters.priceMax);
      if (debouncedFilters.dateFrom) params.dateFrom = debouncedFilters.dateFrom;
      if (debouncedFilters.dateTo) params.dateTo = debouncedFilters.dateTo;

      const data = await adminApi.listProducts(params);
      setResponse(data);
    } catch (error) {
      console.error('Failed to load products:', error);
      setResponse(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = useCallback(async (id: string, published: boolean) => {
    // 1. Optimistically update UI immediately
    setResponse(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map(product =>
          product.id === id
            ? { ...product, published: !published }
            : product
        )
      };
    });

    // 2. Call API in background
    try {
      if (published) {
        await productsApi.unpublish(id);
      } else {
        await productsApi.publish(id);
      }
    } catch (error) {
      // 3. Revert on error
      setResponse(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(product =>
            product.id === id
              ? { ...product, published: published }
              : product
          )
        };
      });
      console.error('Failed to update product status:', error);
      alert('Failed to update product status');
    }
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    // Store original data for potential rollback
    let previousItems: Product[] = [];
    let previousPagination: any = null;

    setResponse(prev => {
      if (!prev) return prev;
      previousItems = prev.items;
      previousPagination = prev.pagination;
      return {
        ...prev,
        items: prev.items.filter(product => product.id !== id),
        pagination: {
          ...prev.pagination,
          total: prev.pagination.total - 1,
          totalPages: Math.ceil((prev.pagination.total - 1) / prev.pagination.limit)
        }
      };
    });

    try {
      await productsApi.delete(id);
    } catch (error) {
      setResponse(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: previousItems,
          pagination: previousPagination
        };
      });
      console.error('Failed to delete product:', error);
      alert('Failed to delete product');
    }
  }, []);

  const handleEdit = useCallback((product: Product) => {
    setSelectedProduct(product);
    setShowForm(true);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedProduct(null);
    setShowForm(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setShowForm(false);
    setSelectedProduct(null);
    loadProducts();
  }, []);

  const handleSort = useCallback((key: string) => {
    const newOrder = filters.sortBy === key && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilter('sortBy', key);
    setFilter('sortOrder', newOrder);
  }, [filters.sortBy, filters.sortOrder, setFilter]);

  // Bulk action handlers - memoized for stable references
  const handleBulkPublish = useCallback(async (published: boolean) => {
    if (bulkProcessing) return;

    try {
      setBulkProcessing(true);
      const result = await adminApi.bulkPublishProducts(Array.from(selectedIds), published);

      if (result.failed > 0) {
        alert(`${published ? 'Published' : 'Unpublished'} ${result.success} products, ${result.failed} failed.\n\nErrors:\n${result.errors.slice(0, 3).join('\n')}`);
      } else {
        alert(`Successfully ${published ? 'published' : 'unpublished'} ${result.success} products`);
      }

      clearSelection();
      await loadProducts();
    } catch (error) {
      console.error('Bulk publish error:', error);
      alert(`Failed to ${published ? 'publish' : 'unpublish'} products: ${error}`);
    } finally {
      setBulkProcessing(false);
    }
  }, [bulkProcessing, selectedIds, clearSelection]);

  const handleBulkDelete = useCallback(async () => {
    if (bulkProcessing) return;

    try {
      setBulkProcessing(true);
      const result = await adminApi.bulkDeleteProducts(Array.from(selectedIds));

      if (result.failed > 0) {
        alert(`Deleted ${result.success} products, ${result.failed} failed.\n\nErrors:\n${result.errors.slice(0, 3).join('\n')}`);
      } else {
        alert(`Successfully deleted ${result.success} products`);
      }

      clearSelection();
      await loadProducts();
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert(`Failed to delete products: ${error}`);
    } finally {
      setBulkProcessing(false);
    }
  }, [bulkProcessing, selectedIds, clearSelection]);

  const handleBulkCategoryChange = async () => {
    if (!newCategory || bulkProcessing) return;

    try {
      setBulkProcessing(true);
      const result = await adminApi.bulkUpdateProducts(Array.from(selectedIds), { category: newCategory });

      if (result.failed > 0) {
        alert(`Updated ${result.success} products, ${result.failed} failed.\n\nErrors:\n${result.errors.slice(0, 3).join('\n')}`);
      } else {
        alert(`Successfully updated category for ${result.success} products`);
      }

      setShowCategoryModal(false);
      setNewCategory('');
      clearSelection();
      await loadProducts();
    } catch (error) {
      console.error('Bulk category change error:', error);
      alert(`Failed to update category: ${error}`);
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkQualityChange = async () => {
    if (!newQualityTier || bulkProcessing) return;

    try {
      setBulkProcessing(true);
      const result = await adminApi.bulkUpdateProducts(Array.from(selectedIds), { qualityTier: newQualityTier });

      if (result.failed > 0) {
        alert(`Updated ${result.success} products, ${result.failed} failed.\n\nErrors:\n${result.errors.slice(0, 3).join('\n')}`);
      } else {
        alert(`Successfully updated quality tier for ${result.success} products`);
      }

      setShowQualityModal(false);
      setNewQualityTier('');
      clearSelection();
      await loadProducts();
    } catch (error) {
      console.error('Bulk quality change error:', error);
      alert(`Failed to update quality tier: ${error}`);
    } finally {
      setBulkProcessing(false);
    }
  };

  // Table column definitions - memoized to prevent unnecessary re-renders
  const columns: Column<Product>[] = useMemo(() => [
    {
      key: 'title',
      header: 'Product',
      sortable: true,
      width: 'w-1/3',
      render: (product: Product) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{product.title}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
            {product.shortDescription}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (product: Product) => (
        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
          {product.category}
        </span>
      ),
    },
    {
      key: 'qualityTier',
      header: 'Quality',
      sortable: true,
      render: (product: Product) => (
        <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded">
          {product.qualityTier}
        </span>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      sortable: true,
      render: (product: Product) => (
        <span className="font-medium">
          {product.price.currency} {product.price.amount.toFixed(2)}
        </span>
      ),
    },
    {
      key: 'published',
      header: 'Status',
      sortable: true,
      render: (product: Product) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded ${
            product.published
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
          }`}
        >
          {product.published ? 'Published' : 'Draft'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (product: Product) => (
        <span className="text-sm">
          {product.createdAt && new Date(product.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: (product: Product) => (
        <div className="flex gap-2">
          <button
            onClick={() => handlePublish(product.id, product.published)}
            className={`p-2 rounded hover:bg-opacity-90 transition-colors ${
              product.published
                ? 'bg-gray-600 hover:bg-gray-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
            title={product.published ? 'Unpublish' : 'Publish'}
          >
            {product.published ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => handleEdit(product)}
            className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(product.id)}
            className="p-2 rounded bg-red-600 hover:bg-red-700 text-white transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], [handlePublish, handleEdit, handleDelete]);

  // Bulk action buttons - memoized to prevent unnecessary re-renders
  const bulkActions: BulkActionButton[] = useMemo(() => [
    {
      label: 'Publish',
      onClick: () => handleBulkPublish(true),
      variant: 'primary',
      disabled: bulkProcessing,
    },
    {
      label: 'Unpublish',
      onClick: () => handleBulkPublish(false),
      variant: 'secondary',
      disabled: bulkProcessing,
    },
    {
      label: 'Change Category',
      onClick: () => setShowCategoryModal(true),
      variant: 'secondary',
      disabled: bulkProcessing,
    },
    {
      label: 'Change Quality',
      onClick: () => setShowQualityModal(true),
      variant: 'secondary',
      disabled: bulkProcessing,
    },
    {
      label: 'Delete',
      onClick: handleBulkDelete,
      variant: 'danger',
      requireConfirmation: true,
      confirmMessage: `Delete ${selectedCount} products? This cannot be undone.`,
      disabled: bulkProcessing,
    },
  ], [bulkProcessing, selectedCount, handleBulkPublish, handleBulkDelete]);

  // These must be BEFORE any early return to satisfy React's rules of hooks
  const products = response?.items || [];
  const pagination = response?.pagination;

  // Calculate stats from current data
  const stats = useMemo(() => {
    const published = products.filter(p => p.published).length;
    const drafts = products.filter(p => !p.published).length;
    const total = pagination?.total || products.length;

    return { total, published, drafts };
  }, [products, pagination]);

  // Stat items for the widget
  const statItems: StatItem[] = useMemo(() => [
    {
      id: 'total',
      label: 'Total Products',
      value: stats.total,
      icon: Package,
      variant: 'primary',
    },
    {
      id: 'published',
      label: 'Published',
      value: stats.published,
      icon: CheckCircle,
      variant: 'success',
    },
    {
      id: 'drafts',
      label: 'Drafts',
      value: stats.drafts,
      icon: FileText,
      variant: 'warning',
    },
  ], [stats]);

  // Early return AFTER all hooks have been called
  if (showForm) {
    return (
      <ProductForm
        product={selectedProduct}
        onClose={handleFormClose}
        onCancel={() => setShowForm(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Product Management
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Create, edit, and manage your product catalog
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadProducts}
            disabled={loading}
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button onClick={handleCreate} className="btn btn-primary inline-flex items-center gap-2">
            <Plus size={16} />
            Add Product
          </button>
        </div>
      </div>

      {/* Stats Section */}
      <section>
        <StatsWidget stats={statItems} columns={3} loading={loading} />
      </section>

      {/* Filters */}
      <FilterBar
        filters={productFilters}
        values={filters}
        onChange={setFilter}
        onClear={clearFilters}
        resultsCount={pagination?.total}
      />

      {/* Product Table */}
      <div className="card">
        <DataTable
          data={products}
          columns={columns}
          selectedIds={selectedIds}
          onSelectChange={toggleSelection}
          onSelectAll={toggleAll}
          onSort={handleSort}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder as 'asc' | 'desc'}
          idKey="id"
          loading={loading}
          emptyMessage={products.length === 0 && !loading ? 'No products found' : ''}
        />
      </div>

      {/* Pagination */}
      {!loading && pagination && pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setFilter('page', page)}
          onItemsPerPageChange={(limit) => setFilter('limit', limit)}
        />
      )}

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />

      {/* Category Change Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-gray-100">
              Change Category for {selectedCount} Products
            </h3>

            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 mb-6"
            >
              <option value="">Select category...</option>
              <option value="horse-racing">Horse Racing</option>
              <option value="greyhound-racing">Greyhound Racing</option>
              <option value="lottery">Lottery</option>
              <option value="card-games">Card Games</option>
              <option value="roulette">Roulette</option>
              <option value="multi-game">Multi-Game</option>
            </select>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setNewCategory('');
                }}
                disabled={bulkProcessing}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkCategoryChange}
                disabled={!newCategory || bulkProcessing}
                className="btn btn-primary"
              >
                {bulkProcessing ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quality Tier Change Modal */}
      {showQualityModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-gray-100">
              Change Quality Tier for {selectedCount} Products
            </h3>

            <select
              value={newQualityTier}
              onChange={(e) => setNewQualityTier(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 mb-6"
            >
              <option value="">Select quality tier...</option>
              <option value="low">Low</option>
              <option value="basic">Basic</option>
              <option value="premium">Premium</option>
            </select>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowQualityModal(false);
                  setNewQualityTier('');
                }}
                disabled={bulkProcessing}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkQualityChange}
                disabled={!newQualityTier || bulkProcessing}
                className="btn btn-primary"
              >
                {bulkProcessing ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Need help with products?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Learn about product setup, pricing strategies, and best practices
              </p>
            </div>
          </div>
          <a href="/help" className="btn btn-secondary whitespace-nowrap">
            View Help Center
          </a>
        </div>
      </section>
    </div>
  );
}

/**
 * Product form component
 */
interface ProductFormProps {
  product: Product | null;
  onClose: () => void;
  onCancel: () => void;
}

function ProductForm({ product, onClose, onCancel }: ProductFormProps): JSX.Element {
  const [formData, setFormData] = useState({
    title: product?.title || '',
    shortDescription: product?.shortDescription || '',
    longDescription: product?.longDescription || '',
    category: product?.category || 'horse-racing',
    qualityTier: product?.qualityTier || 'basic',
    priceAmount: product?.price.amount || 9.99,
    priceCurrency: product?.price.currency || 'USD',
    documentUrl: product?.documentUrl || '',
    imageUrl: product?.imageUrl || '',
    keywords: product?.keywords?.join(', ') || '',
  });
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState<string | null>(null);

  // AI field generation handler
  const handleAiGenerate = async (fieldName: 'title' | 'shortDescription' | 'longDescription' | 'keywords') => {
    if (aiLoading) return;

    setAiLoading(fieldName);
    try {
      // Check if Ollama is available first
      const isAvailable = await checkOllamaConnection();
      if (!isAvailable) {
        alert('Ollama AI server is not running.\n\nTo use AI assistance:\n1. Install Ollama: https://ollama.ai\n2. Run: ollama serve\n3. Pull a model: ollama pull llama3.2');
        return;
      }

      const context: GenerationContext = {
        category: formData.category,
        qualityTier: formData.qualityTier,
        title: formData.title,
        shortDescription: formData.shortDescription,
        longDescription: formData.longDescription,
        keywords: formData.keywords,
        priceAmount: formData.priceAmount,
        priceCurrency: formData.priceCurrency,
      };

      const suggestion = await generateFieldSuggestion(fieldName, context);
      setFormData(prev => ({ ...prev, [fieldName]: suggestion }));
    } catch (error) {
      console.error('AI generation failed:', error);
      alert(`AI generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAiLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Frontend validation
    if (!formData.title.trim()) {
      alert('Product title is required');
      return;
    }

    if (!formData.category) {
      alert('Product category is required');
      return;
    }

    if (!formData.priceAmount || formData.priceAmount <= 0) {
      alert('Valid price amount is required');
      return;
    }

    setSaving(true);

    // Build product data, only including fields with values
    const productData: any = {
      title: formData.title,
      shortDescription: formData.shortDescription,
      longDescription: formData.longDescription,
      category: formData.category,
      qualityTier: formData.qualityTier,
      price: {
        amount: parseFloat(formData.priceAmount.toString()),
        currency: formData.priceCurrency as Currency,
      },
      documentUrl: formData.documentUrl,
      keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k),
      metaTitle: formData.title, // Use title as meta title by default
      metaDescription: formData.shortDescription, // Use short description as meta description
    };

    // Only add imageUrl if it has a value (don't send undefined to Firestore)
    if (formData.imageUrl && formData.imageUrl.trim()) {
      productData.imageUrl = formData.imageUrl;
    }

    try {
      if (product) {
        await productsApi.update(product.id, productData);
      } else {
        await productsApi.create(productData);
      }

      onClose();
    } catch (error: any) {
      console.error('Failed to save product:', error);
      console.error('Product data sent:', productData);

      const errorMessage = error?.message || 'Failed to save product';
      alert(`Failed to save product: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">
          {product ? 'Edit Product' : 'Create Product'}
        </h2>
        <button onClick={onCancel} className="btn btn-secondary">
          ← Back to List
        </button>
      </div>

      {/* Permission Checker - helpful for debugging */}
      {!product && <PermissionChecker />}

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium">
                Product Title *
              </label>
              <AiAssistButton
                onGenerate={() => handleAiGenerate('title')}
                loading={aiLoading === 'title'}
                tooltip="Generate title with AI"
              />
            </div>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium">
                Short Description *
              </label>
              <AiAssistButton
                onGenerate={() => handleAiGenerate('shortDescription')}
                loading={aiLoading === 'shortDescription'}
                disabled={!formData.title}
                tooltip={formData.title ? 'Generate description with AI' : 'Enter a title first'}
              />
            </div>
            <input
              type="text"
              required
              value={formData.shortDescription}
              onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              maxLength={200}
            />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium">
                Long Description *
              </label>
              <AiAssistButton
                onGenerate={() => handleAiGenerate('longDescription')}
                loading={aiLoading === 'longDescription'}
                disabled={!formData.shortDescription}
                tooltip={formData.shortDescription ? 'Generate long description with AI' : 'Enter a short description first'}
              />
            </div>
            <textarea
              required
              value={formData.longDescription}
              onChange={(e) => setFormData({ ...formData, longDescription: e.target.value })}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>
        </div>

        {/* Category & Pricing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Category *</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            >
              <option value="horse-racing">Horse Racing</option>
              <option value="greyhound-racing">Greyhound Racing</option>
              <option value="lottery">Lottery</option>
              <option value="card-games">Card Games</option>
              <option value="roulette">Roulette</option>
              <option value="multi-game">Multi-Game</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Quality Tier *</label>
            <select
              value={formData.qualityTier}
              onChange={(e) => setFormData({ ...formData, qualityTier: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            >
              <option value="low">Low</option>
              <option value="basic">Basic</option>
              <option value="premium">Premium</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Price *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              required
              value={formData.priceAmount}
              onChange={(e) => setFormData({ ...formData, priceAmount: parseFloat(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Currency *</label>
            <select
              value={formData.priceCurrency}
              onChange={(e) => setFormData({ ...formData, priceCurrency: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
        </div>

        {/* Files */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Document URL (GCS Path) *
            </label>
            <input
              type="text"
              required
              placeholder="documents/product-123.pdf"
              value={formData.documentUrl}
              onChange={(e) => setFormData({ ...formData, documentUrl: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            />
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Upload file to Firebase Storage first, then paste the path here
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Image URL (Optional)
            </label>
            <input
              type="text"
              placeholder="images/product-123.jpg"
              value={formData.imageUrl}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
            />
          </div>
        </div>

        {/* Keywords */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="block text-sm font-medium">
              Keywords (comma-separated)
            </label>
            <AiAssistButton
              onGenerate={() => handleAiGenerate('keywords')}
              loading={aiLoading === 'keywords'}
              disabled={!formData.title}
              tooltip={formData.title ? 'Generate keywords with AI' : 'Enter a title first'}
            />
          </div>
          <input
            type="text"
            placeholder="horse racing, betting, strategy"
            value={formData.keywords}
            onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : product ? 'Update Product' : 'Create Product'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
